import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useJobColors } from "../../hooks/useJobColors";
import { CHANGE_HERO, FONT_SIZE, ICON_SIZE, LETTER_SPACING, MOTION } from "../../theme/tokens";
import type { BulkNurseJobsAction, Job } from "../../utils/models";
import { AnimatedNumber } from "../UIToolkit/Animations/AnimatedNumber";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import { ColorDot } from "../UIToolkit/molecules/ColorDot";

type Props = {
	action: BulkNurseJobsAction;
	/** The job being replaced; absent for `add`. */
	fromJob: Job | undefined;
	toJob: Job;
	/** How many employees the apply will change — follows the blocked-employees choice live. */
	willChangeCount: number;
};

/**
 * The review step's one focal moment: how many people this changes, and the change itself as a
 * before → after ribbon. The count springs when the "future shifts" choice below it flips, so the
 * consequence of that choice is visible where the eye already is.
 */
export const BulkJobChangeHero: FC<Props> = ({ action, fromJob, toJob, willChangeCount }) => {
	const { t } = useTranslation();
	const jobColor = useJobColors();
	const ribbonLabel =
		action === "add"
			? t("team.bulk.jobs.hero.ribbon.add", { to: toJob.displayName })
			: t("team.bulk.jobs.hero.ribbon.replace", { from: fromJob?.displayName, to: toJob.displayName });

	return (
		<Box
			data-testid="team-bulk-jobs-hero"
			sx={(theme) => ({
				display: "flex",
				flexWrap: "wrap",
				alignItems: "flex-end",
				justifyContent: "space-between",
				columnGap: 3,
				rowGap: 1.5,
				px: 2.5,
				py: 2,
				borderRadius: 1,
				border: `1px solid ${alpha(theme.palette.primary.main, CHANGE_HERO.BORDER_ALPHA)}`,
				// Diagonal wash from the leading corner: depth under the number without tinting the whole band.
				background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, CHANGE_HERO.WASH_ALPHA)} 0%, ${alpha(theme.palette.primary.main, 0)} ${CHANGE_HERO.WASH_STOP})`,
			})}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
				<Typography
					component="span"
					sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: "primary.dark" }}
				>
					{action === "add" ? t("team.bulk.jobs.hero.eyebrow.add") : t("team.bulk.jobs.hero.eyebrow.replace")}
				</Typography>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
					<Typography
						component="span"
						data-testid="team-bulk-jobs-hero-count"
						sx={{
							fontSize: FONT_SIZE.DISPLAY_LG,
							fontWeight: 700,
							lineHeight: 1,
							letterSpacing: LETTER_SPACING.DISPLAY,
							fontVariantNumeric: "tabular-nums",
							color: "text.primary",
						}}
					>
						{/* Exact on arrival, and settles fast when the choice below changes it — this number is read, not watched. */}
						<AnimatedNumber value={willChangeCount} animateOnMount={false} springConfig={MOTION.SPRING_SETTLE} />
					</Typography>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 500, color: "text.secondary" }}>
						{t("team.bulk.jobs.hero.label", { count: willChangeCount })}
					</Typography>
				</Box>
			</Box>
			<Box role="img" aria-label={ribbonLabel} sx={{ display: "flex", alignItems: "center", gap: 1, pb: 0.75 }} data-testid="team-bulk-jobs-hero-ribbon">
				{action === "add" ? (
					<AddRounded aria-hidden sx={{ fontSize: ICON_SIZE.INLINE, color: "text.secondary" }} />
				) : (
					fromJob && (
						<>
							<MetaChip label={fromJob.displayName} icon={<ColorDot color={jobColor(fromJob.id).dot} />} tone="neutral" size="medium" />
							<ArrowForwardRounded aria-hidden sx={{ fontSize: ICON_SIZE.INLINE, color: "text.secondary", "[dir='rtl'] &": { transform: "scaleX(-1)" } }} />
						</>
					)
				)}
				{/* The job's own colour rides on a dot, as in every job picker — a chip filled with it washes out for the lighter hues. */}
				<MetaChip label={toJob.displayName} icon={<ColorDot color={jobColor(toJob.id).dot} />} tone="primary" size="medium" />
			</Box>
		</Box>
	);
};
